import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SchoolIcon from "@mui/icons-material/School";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { groupdetails } from "../funtionsBD/Mainservice";
import { useBrand } from "../../context/BrandContext";
import { usePermissions } from "../../context/Permissioncontext";
import { resolveClubImageUrl } from "../../utils/resolveClubImageUrl";
import "../GroupMembersOverviewDialog.css";
import "../../pages/admin/Grouplist.css";
import "./GroupDetailsDialog.css";

const COURT_SEATS = 8;

function personName(user, fallback) {
  if (user?.user_alias) return user.user_alias;
  const full = `${user?.user_first_name || ""} ${user?.user_last_name || ""}`.trim();
  return full || fallback;
}

function personInitials(user) {
  if (user?.user_alias) return user.user_alias.slice(0, 2).toUpperCase();
  if (user?.user_first_name && user?.user_last_name) {
    return (user.user_first_name[0] + user.user_last_name[0]).toUpperCase();
  }
  if (user?.user_first_name) return user.user_first_name.slice(0, 2).toUpperCase();
  return "?";
}

function PersonCard({ person, role, t }) {
  const user = person?.tbl_user || {};
  const isCoach = role === "coach";
  const name = personName(user, isCoach ? t("coach") : t("player"));

  return (
    <article className={`gmd-dlg__person${isCoach ? " gmd-dlg__person--coach" : ""}`}>
      {user.user_image ? (
        <img className="gmd-dlg__person-avatar" src={user.user_image} alt="" />
      ) : (
        <div className="gmd-dlg__person-avatar gmd-dlg__person-avatar--fallback">
          {personInitials(user)}
        </div>
      )}
      <div className="gmd-dlg__person-body">
        <p className="gmd-dlg__person-name" title={name}>
          {name}
        </p>
        <div className="gmd-dlg__person-meta">
          <span className={`gmd-dlg__badge${isCoach ? " gmd-dlg__badge--coach" : ""}`}>
            {isCoach ? t("coach") : t("player")}
          </span>
          {!isCoach && (person.fltt_classement || person.fltt_ranking_points != null) && (
            <span className="gmd-dlg__classement">
              {[
                person.fltt_classement,
                person.fltt_ranking_points != null
                  ? `${parseFloat(person.fltt_ranking_points).toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })} ${t("points")}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function CourtGraphic({ seated }) {
  return (
    <div className="gl-court" aria-hidden="true">
      <span className="gl-court__surface" />
      <span className="gl-court__mid" />
      <span className="gl-court__net" />
      {Array.from({ length: COURT_SEATS }, (_, seat) => (
        <span
          key={seat}
          className={`gl-seat gl-seat--${seat}${seat < seated ? " is-on" : ""}`}
        />
      ))}
    </div>
  );
}

export const GroupDetailsDialog = ({ open, onClose, groupId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { brand } = useBrand();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission("UPDATE_GROUP");
  const [loading, setLoading] = useState(false);
  const [groupData, setGroupData] = useState(null);
  const [error, setError] = useState(null);

  const handleEdit = () => {
    if (!groupId) return;
    onClose?.();
    navigate("/EditGroup", { state: { id: groupId } });
  };

  useEffect(() => {
    if (open && groupId) {
      fetchGroupDetails();
    } else {
      setGroupData(null);
      setError(null);
    }
  }, [open, groupId]);

  const fetchGroupDetails = async () => {
    if (!groupId) {
      setError("Group ID is missing");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await groupdetails(groupId);
      if (response.data.status === "success") {
        setGroupData(response.data.data);
      } else {
        setError(response.data.message || "Failed to load group details");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load group details");
    } finally {
      setLoading(false);
    }
  };

  const coaches = groupData?.tbl_coaches || [];
  const players = groupData?.tbl_players || [];
  const clubName =
    brand?.brand_name ||
    groupData?.club_name ||
    groupData?.tbl_club?.club_name ||
    t("club");
  const clubLogo = resolveClubImageUrl(
    brand?.logo_url || groupData?.club_image || groupData?.tbl_club?.club_image
  );
  const seated = Math.min(COURT_SEATS, players.length || 0);
  const mode =
    groupData?.tbl_gen_mode_ts?.ts_gen_mode ||
    groupData?.tbl_gen_mode_ts?.te_gen_mode ||
    groupData?.gr_traning_session_type ||
    "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="gmd-dlg gd-sheet"
      PaperProps={{ className: "gmd-dlg-paper" }}
    >
      <div className={`gd-sheet__club${canUpdate ? " gd-sheet__club--with-edit" : ""}`}>
        <div className="gmd-dlg__header-actions">
          {canUpdate ? (
            <IconButton
              className="gmd-dlg__edit"
              onClick={handleEdit}
              size="small"
              aria-label={t("groupsBoardEdit")}
              title={t("editgroup")}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          ) : null}
          <IconButton className="gmd-dlg__close" onClick={onClose} size="small" aria-label={t("close")}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        {clubLogo ? (
          <img className="gd-sheet__club-mark" src={clubLogo} alt="" />
        ) : (
          <div className="gd-sheet__club-mark gd-sheet__club-mark--fallback">
            {(clubName || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="gd-sheet__club-copy">
          <span className="gd-sheet__club-kicker">{t("groupSheetKicker")}</span>
          <h2 className="gd-sheet__club-name">{clubName}</h2>
        </div>
      </div>

      <div className="gd-sheet__hero">
        <div className="gd-sheet__group">
          <h3 className="gd-sheet__group-name">
            {groupData?.gr_name || t("viewcontent") || "Group"}
          </h3>
          <div className="gd-sheet__meta">
            {mode ? <span>{mode}</span> : null}
            {groupData?.createdAt ? (
              <span>
                {t("createdAt")}: {new Date(groupData.createdAt).toLocaleDateString()}
              </span>
            ) : null}
          </div>
          {groupData?.gr_des ? <p className="gd-sheet__lede">{groupData.gr_des}</p> : null}
        </div>
        <div className="gd-sheet__occupancy">
          <CourtGraphic seated={seated} />
          <div className="gd-sheet__occupancy-stat">
            <span className="gd-sheet__occupancy-n">{players.length}</span>
            <span className="gd-sheet__occupancy-label">{t("players")}</span>
          </div>
        </div>
      </div>

      <DialogContent className="gmd-dlg__body">
        {loading ? (
          <div className="gmd-dlg__loading">
            <CircularProgress size={28} />
            <span>{t("loading")}</span>
          </div>
        ) : error ? (
          <p className="gd-sheet__error">{error}</p>
        ) : groupData ? (
          <div className="gmd-dlg__scroll">
            {coaches.length > 0 && (
              <section className="gmd-dlg__section">
                <div className="gmd-dlg__section-head">
                  <h3 className="gmd-dlg__section-title">
                    <SchoolIcon />
                    {t("coaches")}
                  </h3>
                  <span className="gmd-dlg__count">{coaches.length}</span>
                </div>
                <div className="gmd-dlg__people">
                  {coaches.map((coach, index) => (
                    <PersonCard
                      key={coach.coach_id || coach.tbl_user?.user_id || `coach-${index}`}
                      person={coach}
                      role="coach"
                      t={t}
                    />
                  ))}
                </div>
              </section>
            )}

            {players.length > 0 && (
              <section className="gmd-dlg__section">
                <div className="gmd-dlg__section-head">
                  <h3 className="gmd-dlg__section-title">
                    <PersonIcon />
                    {t("players")}
                  </h3>
                  <span className="gmd-dlg__count">{players.length}</span>
                </div>
                <div className="gmd-dlg__people">
                  {players.map((player, index) => (
                    <PersonCard
                      key={player.player_id || player.tbl_user?.user_id || `player-${index}`}
                      person={player}
                      role="player"
                      t={t}
                    />
                  ))}
                </div>
              </section>
            )}

            {players.length === 0 && coaches.length === 0 && (
              <div className="gmd-dlg__empty">
                <GroupsIcon sx={{ fontSize: 40, opacity: 0.35 }} />
                <p>{t("noMembersInGroup")}</p>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>

      <DialogActions className="gmd-dlg__footer">
        <Button onClick={onClose} className="gmd-dlg__footer-btn" disableElevation>
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
